// src/pages/ServiceWorkspace.jsx
// Full-page Service Workspace - mission control for handling client service issues

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, User, Shield, RefreshCw, ExternalLink, Loader2
} from 'lucide-react';

import ClientQuickReference from '../components/service/ClientQuickReference';
import CarrierCommandCenter from '../components/service/CarrierCommandCenter';
import LiveCallTimer from '../components/service/LiveCallTimer';
import ActiveCasePanel from '../components/service/ActiveCasePanel';
import ServiceHistoryList from '../components/service/ServiceHistoryList';
import SmartActionsBar from '../components/service/SmartActionsBar';
import ClientHealthContext from '../components/service/ClientHealthContext';
import QuickSMSComposer from '../components/service/QuickSMSComposer';
import CarrierChecklist from '../components/service/CarrierChecklist';

const BASE = process.env.REACT_APP_API_URL || '';

export default function ServiceWorkspace() {
  const { clientId } = useParams();

  const [client, setClient] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [matchedCarrier, setMatchedCarrier] = useState(null);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timerData, setTimerData] = useState({});
  const [completedCase, setCompletedCase] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [showSms, setShowSms] = useState(false);
  const [activeCallReason, setActiveCallReason] = useState('');
  const [checklistState, setChecklistState] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const sessionSaveTimer = useRef(null);

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [clientRes, carriersRes, eventsRes, statsRes] = await Promise.all([
        fetch(`${BASE}/clients/${clientId}`, { credentials: 'include' }),
        fetch(`${BASE}/service/carriers`, { credentials: 'include' }),
        fetch(`${BASE}/service/clients/${clientId}/events`, { credentials: 'include' }),
        fetch(`${BASE}/service/analytics/clients/${clientId}`, { credentials: 'include' }),
      ]);

      if (!clientRes.ok) {
        setError('Client not found');
        setLoading(false);
        return;
      }

      const clientData = await clientRes.json();
      const c = clientData.client || clientData.data || clientData;
      console.log('[ServiceWorkspace] clientData keys:', Object.keys(clientData), 'c.carrier:', c.carrier, 'c.plan:', c.plan, 'c.planType:', c.planType);
      setClient(c);

      if (carriersRes.ok) {
        const data = await carriersRes.json();
        const carrierList = data.carriers || [];
        console.log('[ServiceWorkspace] carriers loaded:', carrierList.length, carrierList.map(cr => cr.carrier_name));
        setCarriers(carrierList);

        // Auto-match carrier (priority-based, carrier field only)
        // Match against the carrier field first to avoid false positives from plan names
        // (e.g., "Anthem BCBS" in a plan name should NOT match when carrier is "Humana")
        const normalize = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
        const clientCarrier = (c.carrier || '').toLowerCase().trim();
        const normCarrier = normalize(clientCarrier);
        console.log('[ServiceWorkspace] auto-match inputs:', { clientCarrier, normCarrier });

        if (normCarrier.length > 0 && carrierList.length > 0) {
          // Priority 1: exact normalized match on carrier field
          let matched = carrierList.find(cr => normalize(cr.carrier_name) === normCarrier);

          // Priority 2: carrier name contained in client's carrier field (unidirectional)
          if (!matched) {
            matched = carrierList.find(cr => {
              const cn = normalize(cr.carrier_name);
              return cn.length >= 3 && normCarrier.includes(cn);
            });
          }

          // Priority 3: client carrier field starts with carrier name
          if (!matched) {
            matched = carrierList.find(cr => {
              const cn = normalize(cr.carrier_name);
              return cn.length >= 3 && normCarrier.startsWith(cn);
            });
          }

          if (matched) setMatchedCarrier(matched);
        }
      }

      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEvents(data.events || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.analytics || null);
      }
    } catch (err) {
      console.error('Failed to load workspace data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load existing session on mount (also restores carrier if auto-match missed)
  useEffect(() => {
    fetch(`${BASE}/service/clients/${clientId}/session`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.session) {
          setSessionId(data.session.id);
          if (data.session.call_reason) setActiveCallReason(data.session.call_reason);
          if (data.session.checklist_state) setChecklistState(data.session.checklist_state);
          // Restore carrier from session if auto-match didn't find one
          if (data.session.carrier_id) {
            setMatchedCarrier(prev => {
              if (prev) return prev; // auto-match already found one
              const fromSession = carriers.find(cr => String(cr.id) === String(data.session.carrier_id));
              return fromSession || prev;
            });
          }
        }
      })
      .catch(() => {});
  }, [clientId, carriers]);

  // Auto-save session (debounced 5s)
  const saveSession = useCallback((extraData = {}) => {
    if (sessionSaveTimer.current) clearTimeout(sessionSaveTimer.current);
    sessionSaveTimer.current = setTimeout(() => {
      const body = {
        carrier_id: matchedCarrier?.id || null,
        call_reason: activeCallReason || null,
        checklist_state: checklistState,
        timer_elapsed_seconds: timerData?.elapsed || 0,
        timer_hold_seconds: timerData?.holdTime || 0,
        timer_call_seconds: timerData?.callTime || 0,
        ...extraData,
      };
      fetch(`${BASE}/service/clients/${clientId}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.session?.id) setSessionId(data.session.id); })
        .catch(() => {});
    }, 5000);
  }, [clientId, matchedCarrier, activeCallReason, checklistState, timerData]);

  // Handle new event created
  const handleEventCreated = (event) => {
    setEvents(prev => [event, ...prev]);
    if (event.call_reason) setActiveCallReason(event.call_reason);
    saveSession({ call_reason: event.call_reason, live_notes: '' });
  };

  // Handle outcome logged
  const handleOutcomeLogged = (data) => {
    setCompletedCase(data);
    setActiveCallReason('');
    setChecklistState([]);
    // Complete the session
    if (sessionId) {
      fetch(`${BASE}/service/sessions/${sessionId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ event_id: data.event?.id }),
      }).catch(() => {});
      setSessionId(null);
    }
    // Refresh events to get updated data
    fetch(`${BASE}/service/clients/${clientId}/events`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setEvents(d.events || []))
      .catch(() => {});
  };

  // Handle checklist changes with auto-save
  const handleChecklistChange = useCallback((newState) => {
    setChecklistState(newState);
    saveSession({ checklist_state: newState });
  }, [saveSession]);

  const handleTimerUpdate = useCallback((data) => {
    setTimerData(data);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 size={32} className="text-indigo-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <p className="text-sm text-red-500 mb-3">{error || 'Client not found'}</p>
          <Link to="/service" className="text-sm text-indigo-600 hover:underline">Back to Service Dashboard</Link>
        </div>
      </div>
    );
  }

  const clientName = `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Client';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Back + Client info */}
            <div className="flex items-center gap-3 min-w-0">
              <Link
                to={`/clients/${clientId}`}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                title="Back to profile"
              >
                <ArrowLeft size={18} />
              </Link>
              <div className="min-w-0">
                <h1 className="text-base font-bold text-slate-800 truncate flex items-center gap-2">
                  <User size={16} className="text-indigo-500 flex-shrink-0" />
                  {clientName}
                </h1>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  {matchedCarrier && (
                    <span className="flex items-center gap-1">
                      <Shield size={11} className="text-emerald-500" />
                      {matchedCarrier.carrier_name}
                    </span>
                  )}
                  {client.planType && (
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-semibold text-slate-600">
                      {client.planType}
                    </span>
                  )}
                  {stats && (
                    <span className="text-slate-400">
                      {stats.total_events || 0} service events
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Center: Timer */}
            <div className="hidden sm:block">
              <LiveCallTimer onTimerUpdate={handleTimerUpdate} compact />
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={loadData}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw size={16} />
              </button>
              <Link
                to={`/clients/${clientId}`}
                className="hidden sm:flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                Full Profile <ExternalLink size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* LEFT COLUMN: Reference (4 cols) */}
          <div className="lg:col-span-4 space-y-4">
            <ClientQuickReference client={client} />
            <CarrierCommandCenter
              carrier={matchedCarrier}
              clientPlanType={client.planType}
              carriers={carriers}
              onCarrierSelect={(c) => setMatchedCarrier(c)}
            />
            {/* Carrier Checklist — visible when a case is active */}
            {activeCallReason && matchedCarrier && (
              <CarrierChecklist
                carrier={matchedCarrier}
                callReason={activeCallReason}
                client={client}
                checklistState={checklistState}
                onChecklistChange={handleChecklistChange}
              />
            )}
            <ClientHealthContext clientId={clientId} />
            {/* Mobile-only timer */}
            <div className="sm:hidden">
              <LiveCallTimer onTimerUpdate={handleTimerUpdate} />
            </div>
          </div>

          {/* RIGHT COLUMN: Workspace (8 cols) */}
          <div className="lg:col-span-8 space-y-4">
            {/* Desktop timer */}
            <div className="hidden sm:block lg:hidden">
              <LiveCallTimer onTimerUpdate={handleTimerUpdate} />
            </div>

            <ActiveCasePanel
              clientId={clientId}
              carrier={matchedCarrier}
              carriers={carriers}
              onEventCreated={handleEventCreated}
              onOutcomeLogged={handleOutcomeLogged}
              timerData={timerData}
            />

            {/* Smart Actions - visible after case completion */}
            {completedCase && (
              <SmartActionsBar
                clientId={clientId}
                clientName={clientName}
                event={completedCase.event}
                carrier={completedCase.carrier}
                onNewCase={() => setCompletedCase(null)}
                onSmsSent={(msg) => { setSmsMessage(msg); setShowSms(true); }}
              />
            )}

            <ServiceHistoryList events={events} />
          </div>
        </div>
      </div>

      {/* Quick SMS Composer Modal */}
      {showSms && (
        <QuickSMSComposer
          clientId={clientId}
          clientName={clientName}
          initialMessage={smsMessage}
          onClose={() => { setShowSms(false); setSmsMessage(''); }}
          onSent={() => { setShowSms(false); setSmsMessage(''); }}
        />
      )}
    </div>
  );
}
